import { useMachine } from "@xstate/react";
import { isEmpty, isNil } from "lodash";
import qs from "qs";
import { useCallback, useEffect } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { GainsightEvent, trackEvent } from "../../../core/common/gainsight";
import {
    paymentConfirmedMachine,
    PaymentConfirmedMachineState,
} from "../../../core/workflows/paymentConfirmed";
import {
    paymentRequestMachine,
    PaymentRequestMachineState,
} from "../../../core/workflows/paymentRequest";
import {
    receiptDownloadMachine,
    ReceiptDownloadMachineState,
} from "../../../core/workflows/receiptDownload";
import { ConfirmedPaymentType } from "../../../PaymentContainerClient/commons/enums";
import ConfirmSuccessPaymentResponseDto from "../../../PaymentContainerClient/dtos/ConfirmSuccessPaymentResponseDto";
import getFormattedMessageWithScope from "../../../utils/getFormattedMessageWithScope";
import getCreditorName from "../../commons/getCreditorName";
import routes from "../../commons/routes";
import { FullPageSpinner } from "../../components/FullPageSpinner";
import { Header } from "../../components/Header";
import { PaymentConfirmedSummary } from "../../components/PaymentConfirmedSummary";
import { PaymentFailedSummary } from "../../components/PaymentFailedSummary";
import { PrivacyCookiePolicyFooter } from "../../components/PrivacyCookiePolicyFooter";
import { PrivacyCookiePolicyMenu } from "../../components/PrivacyCookiePolicyMenu";
import { ToastVariant } from "../../components/Toast/Toast";
import { useToast } from "../../components/Toast/ToastProvider";
import firework from "./firework.svg";
import "./style.css";
import womanExclamation from "./womanExclamation.svg";
import womanSitting from "./womanSitting.svg";
import womanSittingError from "./womanSittingError.svg";

const fm = getFormattedMessageWithScope("views.ThankYouPage");
const fme = getFormattedMessageWithScope("errors");

export const ThankYouPage = () => {
    const { token } = useParams();
    const history = useHistory();
    const location = useLocation();
    const { addToast } = useToast();

    const [paymentConfirmedState, sendPaymentConfirmed] = useMachine(
        paymentConfirmedMachine
    );
    const [paymentRequestState, send] = useMachine(paymentRequestMachine);
    const [receiptDownloadState, sendReceiptDownload] = useMachine(
        receiptDownloadMachine.withConfig({
            actions: {
                notifyErrorDownloadingReceipt: () =>
                    addToast(fme("errorDownloadingReceipt"), {
                        variant: ToastVariant.Error,
                        autoDismiss: false,
                    }),
            },
        })
    );

    const goToPaymentRequest = useCallback(() => {
        history.push(routes.home(token));
    }, [token, history]);

    useEffect(() => {
        const { data } = qs.parse(location.search, { ignoreQueryPrefix: true });

        if (isEmpty(data)) {
            goToPaymentRequest();
        }

        if (
            paymentConfirmedState.value === PaymentConfirmedMachineState.Initial
        ) {
            sendPaymentConfirmed("SUBMIT", {
                token: token,
                jwtToken: data,
            });
        }
    }, [
        token,
        paymentConfirmedState.value,
        location.search,
        history,
        sendPaymentConfirmed,
        goToPaymentRequest,
    ]);

    useEffect(() => {
        if (paymentRequestState.value === PaymentRequestMachineState.Idle) {
            send("FETCH", { token });
        }
    }, [token, paymentRequestState.value, send]);

    function isSuccessPayment(
        paymentConfirmed?: any
    ): paymentConfirmed is ConfirmSuccessPaymentResponseDto {
        return paymentConfirmed && paymentConfirmed.state !== "failed";
    }

    function downloadReceipt() {
        if (isSuccessPayment(paymentConfirmedState.context.data)) {
            trackEvent(GainsightEvent.DownloadReceipt);
            sendReceiptDownload("DOWNLOAD", {
                url: paymentConfirmedState.context.data?.receipt,
            });
        }
    }

    const isLoading =
        paymentRequestState.value === PaymentRequestMachineState.Loading ||
        paymentConfirmedState.value === PaymentConfirmedMachineState.Submitting;

    const creditorName = paymentRequestState.context.data
        ? getCreditorName(paymentRequestState.context.data?.creditor)
        : paymentConfirmedState.context.data?.companyName;

    return (
        <FullPageSpinner isLoading={isLoading} message={fm("loading")}>
            {paymentConfirmedState.value ===
            PaymentConfirmedMachineState.Success ? (
                <div className="v-ThankYouPage-container">
                    <Header
                        creditorName={isLoading ? undefined : creditorName}
                        logoUrl={
                            isLoading
                                ? undefined
                                : paymentRequestState.context.data?.creditor
                                      .logoUrl
                        }
                    />
                    <div className="v-ThankYouPage-content">
                        {isSuccessPayment(
                            paymentConfirmedState.context.data
                        ) ? (
                            <>
                                <img
                                    alt="Firework"
                                    className="v-ThankYouPage-content-fireworkMiddle-image"
                                    src={firework}
                                />
                                <img
                                    alt="Firework"
                                    className="v-ThankYouPage-content-fireworkSmall-image"
                                    src={firework}
                                />
                                <PaymentConfirmedSummary
                                    creditorName={
                                        isLoading ? undefined : creditorName
                                    }
                                    isBankTransfer={isNil(
                                        paymentConfirmedState.context.data
                                            ?.brand
                                    )}
                                    isPayNow={
                                        paymentConfirmedState.context.data
                                            ?.type ===
                                        ConfirmedPaymentType.Link2Pay
                                    }
                                    paymentReason={
                                        paymentConfirmedState.context.data
                                            ?.paymentRef ||
                                        paymentRequestState.context.data
                                            ?.paymentReason
                                    }
                                    amount={
                                        paymentConfirmedState.context.data
                                            ?.amount
                                    }
                                    currency={
                                        isLoading ||
                                        !paymentConfirmedState.context.data
                                            ? "EUR"
                                            : paymentConfirmedState.context.data
                                                  .currency
                                    }
                                    brand={
                                        paymentConfirmedState.context.data
                                            ?.brand
                                    }
                                    cardExpirationDate={
                                        paymentConfirmedState.context.data
                                            ?.expiration
                                    }
                                    panIban={
                                        paymentConfirmedState.context.data
                                            ?.panIban
                                    }
                                    onGoToPaymentRequest={goToPaymentRequest}
                                    onDownloadReceipt={downloadReceipt}
                                    isDownloadingReceipt={
                                        receiptDownloadState.value ===
                                        ReceiptDownloadMachineState.Loading
                                    }
                                    installments={
                                        paymentConfirmedState.context.data?.plan
                                            ?.schedules
                                    }
                                    singleScheduledPayment={
                                        paymentConfirmedState.context.data?.plan
                                            ?.oneTime
                                    }
                                />
                                <img
                                    alt="Firework"
                                    className="v-ThankYouPage-content-fireworkBig-image"
                                    src={firework}
                                />
                                <img
                                    alt="Woman"
                                    className="v-ThankYouPage-content-woman-image"
                                    src={womanSitting}
                                />
                            </>
                        ) : (
                            <>
                                <PaymentFailedSummary
                                    creditorName={
                                        isLoading ? undefined : creditorName
                                    }
                                    onGoToPaymentRequest={goToPaymentRequest}
                                    paymentReason={
                                        paymentConfirmedState.context.data
                                            ?.paymentRef ||
                                        paymentRequestState.context.data
                                            ?.paymentReason
                                    }
                                    paymentError={
                                        paymentConfirmedState.context.data
                                            ?.errorMessage
                                    }
                                />
                                <img
                                    alt="Woman"
                                    className="v-ThankYouPage-content-woman-error-image"
                                    src={womanSittingError}
                                />
                            </>
                        )}
                    </div>
                    <PrivacyCookiePolicyFooter />
                </div>
            ) : paymentConfirmedState.value ===
              PaymentConfirmedMachineState.Failure ? (
                <>
                    <div className="v-ThankYouPage-error-PrivacyCookiePolicyMenu-container">
                        <PrivacyCookiePolicyMenu />
                    </div>
                    <div className="v-ThankYouPage-error-container">
                        <img src={womanExclamation} alt="error" />
                        <p className="v-ThankYouPage-error-message">
                            {fm("error")}
                        </p>
                        <PrivacyCookiePolicyFooter />
                    </div>
                </>
            ) : undefined}
        </FullPageSpinner>
    );
};
